// AnimateTopBar.tsx
import React, { useState, useRef, useEffect } from "react";
import styled from "styled-components";
import Colors from "../../../ui/global/Colors";
import Fonts from "../../../ui/global/Fonts";
import Icon from "../../../ui/global/Icon";
import { useConfig } from "../../../utils/config";
import Button from "../../../ui/components/Button";
import ButtonLoad from "../../../ui/components/ButtonLoad";
import Tooltip from "../../../ui/components/Tooltip";
import InputSimple from "../../../ui/components/InputSimple";
import { useNavigate } from "react-router-dom";
import HomeLogoButton from "../../shared/HomeLogoButton";
import ProfileButton from "../../shared/ProfileButton";
import ExportMenu from "../components/ExportMenu";
import { SpriteSheetCalculation } from "../utils/calculateSpriteSheet";
import { AnimateActionType } from "../hooks/useAnimateActions";
import MenuOpen from "../../../ui/components/MenuOpen";
import { useCreditsContext } from "../../pricing/CreditsContext";

const TopContainer = styled.div`
    position: relative;
    width: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;
    padding: 0;
    margin: 0;
    // background-color: ${Colors.white};
    // box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.04);  
    // border-bottom: 1px solid ${Colors.grey1};   
    // flex-shrink: 0;
`;

const Toolbar = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    height: fit-content;
    margin: 0;
    padding: 0 5px 5px 5px;
    gap: 10px;
    z-index: 100;
    overflow: visible;
`;

const Left = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: flex-start;
    gap: 6px;
    margin: 0;
`;

const Middle = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    flex: 1;
    gap: 6px;
    margin: 0;
`;

const Right = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: flex-end;
    gap: 6px;
    margin: 0;
`;

const Divider = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: ${Colors.grey1};
    width: 1px;
    height: 24px;
`;

const Name = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    font-family: ${Fonts.lexendSemiLight.fontFamily};
    font-weight: ${Fonts.lexendSemiLight.fontWeight};
    font-size: 14px;
    line-height: 1.2em;
    background-color: ${Colors.transparent};
    color: ${Colors.black};
    border: 1px solid ${Colors.transparent};
    text-wrap: nowrap;
    border-radius: 8px;
    // margin: 10px;

    &:hover {
        background-color: ${Colors.white};
        border: 1px solid ${Colors.grey1};
    }
`;

const GeneratingContainer = styled.div<{ $isExportMenuOpen: boolean }>`
    opacity: ${props => props.$isExportMenuOpen ? 0.28 : 1};
`;

interface AnimateTopBarProps {
    animationId: string;
    initialName: string;
    spriteSheetDefaults: SpriteSheetCalculation;
    totalFrames: number;
    currentAction: AnimateActionType | null;
    onExport: (formatId: string, settings: any) => void;
    progress: number;
    message: string;
    onRemixClick: () => void;
    handleAnimationNameChange: (newName: string) => void;
    visibility: 'public' | 'private';
    onVisibilityChange: (newVisibility: 'public' | 'private') => void;
}

const AnimateTopBar = ({ 
    animationId, 
    initialName, 
    spriteSheetDefaults, 
    totalFrames, 
    currentAction, 
    onExport, 
    progress, 
    message, 
    onRemixClick, 
    handleAnimationNameChange,
    visibility = 'public',
    onVisibilityChange = () => {}, 
}: AnimateTopBarProps) => {
    const config = useConfig();
    const navigate = useNavigate();
    const { subscription } = useCreditsContext();
    const [animationName, setAnimationName] = useState(initialName);

    // Ref to hold the wrapper div element for anchoring the menu
    const exportButtonWrapperRef = useRef<HTMLDivElement | null>(null);

    // State for the Export Menu
    const [exportMenuAnchorEl, setExportMenuAnchorEl] = useState<HTMLElement | null>(null);
    const isExportMenuOpen = Boolean(exportMenuAnchorEl);

    // State for the More Menu
    const [moreMenuAnchorEl, setMoreMenuAnchorEl] = useState<HTMLElement | null>(null);
    const [isRemixing, setIsRemixing] = useState(false);

    useEffect(() => {
        setAnimationName(initialName);
    }, [initialName]);

    const onHomeClick = () => {
        navigate('/animate');
    };

    const onNameChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const currentValue = e.target.value;
        setAnimationName(currentValue); // Immediate local update
        handleAnimationNameChange(currentValue); // Notify parent to debounce/save
    };

    const handleExportMenuClose = () => {
        setExportMenuAnchorEl(null);
    };
    
    // Updated handler to toggle the menu on button click, using the wrapper ref
    const handleExportButtonClick = () => {
        if (isExportMenuOpen) {
            handleExportMenuClose();
        } else if (exportButtonWrapperRef.current) {
            // Set the menu anchor to the wrapper div element via ref
            setExportMenuAnchorEl(exportButtonWrapperRef.current);
        }
    };

    const handleMoreClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        if (moreMenuAnchorEl) {
            setMoreMenuAnchorEl(null);
        } else {
            setMoreMenuAnchorEl(event.currentTarget);
        }
    };

    const handleMoreMenuClose = () => {
        setMoreMenuAnchorEl(null);
    };

    const onRemixMenuClick = async () => {
        handleMoreMenuClose();
        setIsRemixing(true);
        try {
            await onRemixClick();
        } finally {
            setIsRemixing(false);
        }
    };

    const onVisibilityToggle = async () => {
        const newVisibility = visibility === 'public' ? 'private' : 'public';
        try {
            // await updateAnimation(animationId, { visibility: newVisibility });
            onVisibilityChange(newVisibility);
        } catch (error) {
            console.error('Error updating visibility:', error);
        }
        handleMoreMenuClose();
    };

    const isPaid = (subscription === 'creator' || subscription === 'hobby');

    const menuOptions = [
        { 
            text: 'Visibility', 
            text_right: visibility === 'public' ? 'Public' : 'Private', 
            icon: visibility === 'public' ? 'lock' : 'eye', 
            onClick: onVisibilityToggle,
            disabled: !isPaid,
            tooltip: !isPaid ? ({ sub_text: "Available on paid plans", colorMode: "light", position: "left" } as const) : undefined
        },
        { text: 'Duplicate Animation', icon: 'swap', onClick: onRemixMenuClick, disabled: isRemixing },
    ];

    const renderLeft = () => {
        return (
            <Left>
                <HomeLogoButton onHomeClick={onHomeClick} linkTo="/animate" colorMode="light" />
                {/* <Divider /> */}
                <Name>
                    <InputSimple
                        value={animationName}
                        colorMode='light'
                        textAlign='left'
                        onChange={onNameChange}
                        onEnterPress={() => {}}
                    />
                </Name>
                {/* <Button 
                    size="micro" 
                    color="ghost" 
                    icon="more" 
                    onClick={()=>{}}
                /> */}
            </Left>
        )
    };

    const renderMiddle = () => {
        return (
        <>
            {/* <Divider /> */}
            <Middle>
                
            </Middle>
            {/* <Divider /> */}
        </>
        )
    };

    const renderRight = () => {
        return (
            <Right>
                {/* <Button 
                    size="micro" 
                    color="ghost" 
                    icon="undo" 
                    onClick={()=>{}}
                />
                <Button 
                    size="micro" 
                    color="ghost" 
                    icon="redo" 
                    onClick={()=>{}}
                /> */}
                
                <>
                    <GeneratingContainer ref={exportButtonWrapperRef} $isExportMenuOpen={isExportMenuOpen}>
                        <ButtonLoad
                            size="micro" 
                            color="black"
                            text={currentAction === 'export' ? 'Exporting...' : 'Export'}
                            onClick={handleExportButtonClick}
                            disabled={totalFrames === 0 || currentAction !== null}
                            progress={currentAction === 'export' ? progress : undefined}
                        />
                    </GeneratingContainer>
                    <ExportMenu
                        anchorEl={exportMenuAnchorEl}
                        open={isExportMenuOpen}
                        onClose={handleExportMenuClose}
                        onMenuLeave={handleExportMenuClose}
                        autoRows={spriteSheetDefaults.rows}
                        autoCols={spriteSheetDefaults.cols}
                        autoSheetWidth={spriteSheetDefaults.sheetWidth}
                        autoSheetHeight={spriteSheetDefaults.sheetHeight}
                        totalFrames={totalFrames}
                        onExport={onExport}
                        isExporting={currentAction === 'export'}
                    />
                </>

                <Button size="micro" color="ghost" icon="more" onClick={handleMoreClick} />
                <MenuOpen
                    anchorEl={moreMenuAnchorEl}
                    open={Boolean(moreMenuAnchorEl)}
                    onClose={handleMoreMenuClose}
                    options={menuOptions}
                    minWidth={200}
                    color="white"
                />

                <ProfileButton config={config} colorMode="light" />
            </Right>
        )
    };

    return (
        <TopContainer>
            <Toolbar id='toolbar-top'>
                {renderLeft()}
                {window.innerWidth > 768 && renderMiddle()}
                {renderRight()}
            </Toolbar>
        </TopContainer>
    );
};

export default AnimateTopBar;